<script setup lang="ts">
import { computed } from 'vue'
import { formatMoney } from '@/lib/utils/money'
import { useCategoryRefund } from '@/composables/useCategoryRefund'

interface Props {
  /**
   * Direct amount to display. If provided, takes precedence.
   */
  amount?: number | (() => number)

  /**
   * Form 11 category key for server-side calculation.
   * When specified, the component will query the backend and ignore `amount`.
   */
  category?: string

  /**
   * Data payload for the calculator. Defaults to empty object.
   */
  payload?: Record<string, any>

  /** Tax year to use. Defaults to current year. */
  year?: number
}

const props = withDefaults(defineProps<Props>(), {
  amount: undefined,
  category: undefined,
  payload: undefined,
  year: undefined,
})

// Decide how to obtain the amount
let amountRef: any
if (props.category) {
  // Ensure payload changes propagate by passing a computed ref
  const payloadRef = computed(() => props.payload ?? {})

  const calc = useCategoryRefund(
    props.year ?? new Date().getFullYear(),
    props.category,
    payloadRef,
  )
  amountRef = calc.amount
} else {
  amountRef = computed(() => {
    const raw: any =
      typeof props.amount === 'function' ? props.amount() : props.amount
    return raw && typeof raw === 'object' && 'value' in raw
      ? raw.value
      : (raw ?? 0)
  })
}
</script>

<template>
  <div class="bg-muted p-4 rounded-lg">
    <p class="font-medium">Available to claim</p>
    <p class="text-2xl font-bold">{{ formatMoney(amountRef) }}</p>
  </div>
</template>
